import { setClass, useOutsideClick } from "@utils";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowBottom, Cross } from "../svgs";
import { SelectContent } from "./SelectContent";
import { SelectOptions } from "./SelectOptions";
import '../../styles/components/select2.scss';

interface SelectCommonProps<T> {
  value: T[] | T | null;
  multiple?: boolean;
  options: T[];
  placeholder: string;
  classNameRootWrapper?: string;
  search?: boolean;
  disabled?: boolean;
  placeholderWhenNoFilteredItems?: string;
  commonContent?: boolean;
  defaultValue?: T;
  smallArrow?: boolean;
  onChange: (value: T[] | T) => void;
  renderOption?: (item: T, isSelected: boolean) => JSX.Element;
  getOptionLabel?: (option: Record<string, any> | string | number) => string;
  getOptionId?: (option: Record<string, any> | string | number) => string | number;
  onCleared?: () => void;
  clearOnSelect?: boolean;
  customOption?: string;
  variant?: 'underlined' | 'square';
  customTimeOptions?: string[];
  error?: boolean;
  errorText?: string;
}

export const Select = <T,>({
  placeholderWhenNoFilteredItems, commonContent, defaultValue, customTimeOptions,
  customOption, placeholder, search, classNameRootWrapper, smallArrow,
  multiple = false,
  options = [],
  value = multiple ? [] : null,
  disabled = false,
  clearOnSelect = true,
  onChange,
  onCleared,
  renderOption = (option: T) => <>{getOptionLabel(option)}</>,
  getOptionLabel = (option) => typeof option === 'object' ? option?.name : option,
  getOptionId = (option) => typeof option === 'object' ? option?.id : option,
  variant = 'square',
  error = false,
  errorText,
}: SelectCommonProps<T>) => {

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchValue, setSearchValue] = useState('');
  const ref = useOutsideClick(() => setMenuOpen(false));

  useEffect(() => {
    if (search) {
      setFilteredOptions(options.filter(elem =>
        getOptionLabel(elem).toLowerCase().includes(searchValue.toLowerCase())
      ));
    } else {
      setFilteredOptions(options);
    }
    if (searchValue) setMenuOpen(true);
  }, [options, searchValue]);

  const showMenu = () => (
    !disabled && setMenuOpen(prev => !prev)
  );

  const clearValue = (e: any) => {
    e.stopPropagation();
    if (onCleared) onCleared();
    setMenuOpen(false);
    setSearchValue('');
  };

  const onChangeSearch = (value: string) => (
    setSearchValue(value)
  );

  const isShowCross = useMemo(() => {
    const isShow = !!(Array.isArray(value) ? value.length : value) && onCleared || searchValue;
    return defaultValue
      ? isShow && getOptionId(defaultValue) !== getOptionId(value)
      : isShow;
  }, [searchValue, value]);

  return (
    <div
      className={setClass('select-wrapper', classNameRootWrapper)}
      ref={ref}
    >
      <div className='input-wrapper' onClick={showMenu}>
        <div className={setClass("input", variant, isMenuOpen && 'open', disabled && 'disabled', error && 'error')}>
          <SelectContent
            value={value}
            placeholder={placeholder}
            multiple={multiple}
            search={search}
            searchValue={searchValue}
            isMenuOpen={isMenuOpen}
            commonContent={commonContent}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            getOptionId={getOptionId}
            onChangeSearch={onChangeSearch}
            smallArrow={smallArrow}
          />
        </div>
        {isShowCross
          ? (
            <Cross 
              divClassName="clear"
              onClick={clearValue} 
              width={14}
              height={14}
            />
          )
          : (
            <ArrowBottom 
              divClassName={setClass("arrow-icon", variant, isMenuOpen && 'open')}
              small={smallArrow}
            />
          )
        }
      </div>
      <SelectOptions 
        customTimeOptions={customTimeOptions}
        disabled={disabled}
        isMenuOpen={isMenuOpen}
        filteredOptions={filteredOptions}
        value={value}
        renderOption={renderOption}
        getOptionId={getOptionId}
        placeholderNoFiltered={placeholderWhenNoFilteredItems}
        customOption={customOption}
        onChange={onChange}
        defaultValue={defaultValue}
        multiple={multiple} 
        setMenuOpen={setMenuOpen} 
        setSearchValue={setSearchValue} 
        clearOnSelect={clearOnSelect}   
        ref={ref}   
      />
      {error && <p className="error-text">{errorText}</p>}
    </div>
  );
};
